/**
 * Growth rollup — turns the `Goal → Milestone → Target → Habit` chain plus
 * open blockers into one calm, non-shaming progress signal for a goal.
 *
 * It is PURE: the data layer gathers a goal's linked entities (via the
 * relationship graph) and their current states, then this function composes a
 * single score + health word. No guilt scores: rest days and paused items are
 * treated as neutral, and blockers lower the signal rather than inflating it.
 */

export type MilestoneStatusLite = 'open' | 'in_progress' | 'done';
export type TargetStateLite = 'on_track' | 'in_progress' | 'needs_attention' | 'paused';
export type HabitStateLite = 'on_track' | 'due_today' | 'resting' | 'paused';

export type GoalHealth = 'on_track' | 'at_risk' | 'blocked' | 'no_signal';

export interface GoalRollupInput {
  readonly milestoneStatuses: readonly MilestoneStatusLite[];
  readonly targetStates: readonly TargetStateLite[];
  readonly habitStates: readonly HabitStateLite[];
  /** Count of OPEN problems linked as blockers of this goal. */
  readonly openBlockers: number;
}

export interface GoalRollup {
  readonly milestonesDone: number;
  readonly milestonesTotal: number;
  readonly milestoneCompletion: number;
  readonly targetsOnTrack: number;
  readonly targetsActive: number;
  readonly habitsOnTrack: number;
  readonly habitsActive: number;
  readonly openBlockers: number;
  readonly score: number;
  readonly health: GoalHealth;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

function targetCredit(s: TargetStateLite): number {
  if (s === 'on_track') return 1;
  if (s === 'in_progress') return 0.5;
  return 0; // needs_attention
}

function habitCredit(s: HabitStateLite): number {
  if (s === 'on_track') return 1;
  if (s === 'resting') return 1; // a scheduled rest day is not a failure
  if (s === 'due_today') return 0.5;
  return 0;
}

/** Compose a goal's linked signals into a 0–100 score and a health word. Pure. */
export function computeGoalRollup(input: GoalRollupInput): GoalRollup {
  const milestonesTotal = input.milestoneStatuses.length;
  const milestonesDone = input.milestoneStatuses.filter((s) => s === 'done').length;
  const milestoneCompletion = milestonesTotal === 0 ? 0 : milestonesDone / milestonesTotal;

  const activeTargets = input.targetStates.filter((s) => s !== 'paused');
  const targetsActive = activeTargets.length;
  const targetsOnTrack = input.targetStates.filter((s) => s === 'on_track').length;
  const targetRatio = targetsActive === 0 ? null : activeTargets.reduce((a, s) => a + targetCredit(s), 0) / targetsActive;

  const activeHabits = input.habitStates.filter((s) => s !== 'paused');
  const habitsActive = activeHabits.length;
  const habitsOnTrack = input.habitStates.filter((s) => s === 'on_track').length;
  const habitRatio = habitsActive === 0 ? null : activeHabits.reduce((a, s) => a + habitCredit(s), 0) / habitsActive;

  const components: number[] = [];
  if (milestonesTotal > 0) components.push(milestoneCompletion);
  if (targetRatio !== null) components.push(targetRatio);
  if (habitRatio !== null) components.push(habitRatio);

  const openBlockers = Math.max(0, Math.trunc(input.openBlockers));
  const hasSignal = components.length > 0;

  let score = 0;
  if (hasSignal) {
    const base = components.reduce((a, c) => a + c, 0) / components.length;
    const penalty = Math.min(openBlockers * 0.15, 0.4);
    score = clamp(Math.round((base - penalty) * 100), 0, 100);
  }

  let health: GoalHealth;
  if (!hasSignal && openBlockers === 0) {
    health = 'no_signal';
  } else if (openBlockers > 0 && score < 40) {
    health = 'blocked';
  } else if (score >= 70 && openBlockers === 0) {
    health = 'on_track';
  } else {
    health = 'at_risk';
  }

  return {
    milestonesDone,
    milestonesTotal,
    milestoneCompletion,
    targetsOnTrack,
    targetsActive,
    habitsOnTrack,
    habitsActive,
    openBlockers,
    score,
    health,
  };
}
